import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import type { PublicUser } from '@shared/api';
import { LIMITS } from '@shared/constants';
import { CardForm, type CardFormHandle } from '../components/LazyCardForm';
import { Notice } from '../components/Notice';
import { formatPrice } from '../components/UpgradeDialog';
import { errorMessage, post } from '../lib/api';
import { useSession } from '../state/session';

/** The landing page (with the legal pages) lives on the main domain; the app on app.<domain>. */
function siteUrl(path: string): string {
  const host = window.location.hostname;
  return host.startsWith('app.') ? `https://${host.slice(4)}${path}` : path;
}

const GOOGLE_ERRORS: Record<string, string> = {
  google_not_configured: 'O login com Google não está disponível.',
  google_failed: 'Não foi possível entrar com o Google. Tente novamente.',
  google_email_unverified: 'Sua conta Google não tem e-mail verificado.',
};

function AuthLayout({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <div className="auth">
      <main className="auth__card">
        <div className="auth__brand">
          <img src="/icons/icon-192x192.png" alt="" width={64} height={64} />
          <p className="auth__app">Voz Livre App</p>
        </div>
        <h1>{title}</h1>
        {subtitle && <p className="muted">{subtitle}</p>}
        {children}
        <p className="auth__footer small muted">Comunicação Aumentativa e Alternativa · Jornada TEAjudo</p>
      </main>
    </div>
  );
}

function GoogleButton() {
  const { config } = useSession();
  if (!config?.googleEnabled) return null;
  return (
    <>
      <div className="divider">
        <span>ou</span>
      </div>
      <a className="btn btn--google btn--block" href="/auth/google">
        <svg aria-hidden="true" width="18" height="18" viewBox="0 0 48 48">
          <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
          <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
          <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
          <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
        </svg>
        Entrar com Google
      </a>
    </>
  );
}

export function LoginPage() {
  const { setUser } = useSession();
  const [, navigate] = useLocation();
  const emailId = useId();
  const passwordId = useId();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleted] = useState(() => new URLSearchParams(window.location.search).get('conta') === 'excluida');
  const [passwordChanged] = useState(() => new URLSearchParams(window.location.search).get('senha') === 'alterada');

  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get('error');
    if (code) setError(GOOGLE_ERRORS[code] ?? 'Não foi possível entrar.');
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { user } = await post<{ user: PublicUser }>('/api/auth/login', { email, password });
      setUser(user);
      navigate('/', { replace: true });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Entrar" subtitle="Acesse sua conta para usar o quadro de comunicação.">
      {deleted && <Notice kind="success">Sua conta e seus dados foram excluídos.</Notice>}
      {passwordChanged && <Notice kind="success">Senha alterada. Entre com a nova senha.</Notice>}
      <form className="form" onSubmit={submit} noValidate>
        <div className="field">
          <label htmlFor={emailId}>E-mail</label>
          <input id={emailId} type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor={passwordId}>Senha</label>
          <input
            id={passwordId}
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {error && <Notice kind="error">{error}</Notice>}
        <button type="submit" className="btn btn--primary btn--block btn--lg" disabled={busy}>
          {busy ? 'Entrando…' : 'Entrar'}
        </button>
        <p className="auth__forgot">
          <Link href="/esqueci-senha">Esqueci minha senha</Link>
        </p>
      </form>
      <GoogleButton />
      <p className="auth__switch">
        Ainda não tem conta? <Link href="/cadastro">Criar conta</Link>
      </p>
    </AuthLayout>
  );
}

export function RegisterPage() {
  const { setUser, config } = useSession();
  const [, navigate] = useLocation();
  const ids = { name: useId(), email: useId(), password: useId(), card: useId() };
  const cardRef = useRef<CardFormHandle>(null);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const billing = config?.billing;
  const needsCard = !!billing?.requireCardAtSignup && !!billing.publishableKey;
  const trialDays = config?.trialDays ?? 45;
  const price = billing?.price;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) return setError('Informe seu nome.');
    if (form.password.length < LIMITS.minPasswordLength) {
      return setError(`A senha deve ter pelo menos ${LIMITS.minPasswordLength} caracteres.`);
    }
    setBusy(true);
    try {
      let paymentMethodId: string | undefined;
      if (needsCard) {
        if (!cardRef.current) throw new Error('O formulário do cartão ainda está carregando.');
        paymentMethodId = await cardRef.current.createPaymentMethod({ name: form.name, email: form.email });
      }
      const { user } = await post<{ user: PublicUser }>('/api/auth/register', { ...form, paymentMethodId });
      setUser(user);
      navigate('/', { replace: true });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <AuthLayout title="Criar conta" subtitle={`Experimente grátis por ${trialDays} dias.`}>
      <form className="form" onSubmit={submit} noValidate>
        <div className="field">
          <label htmlFor={ids.name}>Nome</label>
          <input id={ids.name} autoComplete="name" required maxLength={100} value={form.name} onChange={set('name')} />
        </div>
        <div className="field">
          <label htmlFor={ids.email}>E-mail</label>
          <input id={ids.email} type="email" autoComplete="email" required value={form.email} onChange={set('email')} />
        </div>
        <div className="field">
          <label htmlFor={ids.password}>Senha</label>
          <input
            id={ids.password}
            type="password"
            autoComplete="new-password"
            required
            minLength={LIMITS.minPasswordLength}
            value={form.password}
            onChange={set('password')}
            aria-describedby={`${ids.password}-hint`}
          />
          <p id={`${ids.password}-hint`} className="muted small">
            Mínimo de {LIMITS.minPasswordLength} caracteres.
          </p>
        </div>
        {needsCard && (
          <div className="field">
            <span className="field__label" id={ids.card}>
              Cartão de crédito
            </span>
            <CardForm ref={cardRef} publishableKey={billing!.publishableKey!} />
            <p className="muted small">
              Nada é cobrado durante os {trialDays} dias grátis.
              {price && ` Depois, ${formatPrice(price.amount, price.currency)}/mês. Cancele quando quiser.`}
            </p>
          </div>
        )}
        {error && <Notice kind="error">{error}</Notice>}
        <button type="submit" className="btn btn--primary btn--block btn--lg" disabled={busy}>
          {busy ? 'Criando conta…' : 'Criar conta'}
        </button>
        <p className="muted small consent">
          Ao criar a conta, você concorda com os{' '}
          <a href={siteUrl('/termos')} target="_blank" rel="noopener">
            Termos de Uso
          </a>{' '}
          e a{' '}
          <a href={siteUrl('/privacidade')} target="_blank" rel="noopener">
            Política de Privacidade
          </a>
          .
        </p>
      </form>
      <GoogleButton />
      <p className="auth__switch">
        Já tem conta? <Link href="/login">Entrar</Link>
      </p>
    </AuthLayout>
  );
}

const SUPPORT_WHATSAPP = 'https://wa.me/5511945276535';

export function ForgotPasswordPage() {
  const { config } = useSession();
  const emailId = useId();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post('/api/auth/forgot', { email });
      setSent(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Esqueci minha senha" subtitle={config?.passwordResetByEmail ? 'Enviaremos um link para você criar uma nova senha.' : undefined}>
      {!config ? null : !config.passwordResetByEmail ? (
        <>
          <Notice kind="info">Para criar uma nova senha, fale com o suporte. Enviamos um link pelo WhatsApp.</Notice>
          <a className="btn btn--primary btn--block btn--lg" href={SUPPORT_WHATSAPP} target="_blank" rel="noopener">
            Falar no WhatsApp
          </a>
        </>
      ) : sent ? (
        <Notice kind="success">
          Se <b>{email}</b> estiver cadastrado, você vai receber em instantes um e-mail com o link para criar a nova senha. Confira também a caixa
          de spam. O link vale por 1 hora.
        </Notice>
      ) : (
        <form className="form" onSubmit={submit} noValidate>
          <div className="field">
            <label htmlFor={emailId}>E-mail da conta</label>
            <input id={emailId} type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          {error && <Notice kind="error">{error}</Notice>}
          <button type="submit" className="btn btn--primary btn--block btn--lg" disabled={busy || !email.trim()}>
            {busy ? 'Enviando…' : 'Enviar link'}
          </button>
        </form>
      )}
      <p className="auth__switch">
        <Link href="/login">Voltar para entrar</Link>
      </p>
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const { refresh } = useSession();
  const [, navigate] = useLocation();
  const ids = { password: useId(), confirm: useId() };
  const [token] = useState(() => new URLSearchParams(window.location.search).get('token') ?? '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < LIMITS.minPasswordLength) return setError(`A senha deve ter pelo menos ${LIMITS.minPasswordLength} caracteres.`);
    if (password !== confirm) return setError('As duas senhas não são iguais.');
    setBusy(true);
    try {
      await post('/api/auth/reset', { token, password });
      // The server ended every session of the account; sign in with the new password.
      await refresh();
      navigate('/login?senha=alterada', { replace: true });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthLayout title="Criar nova senha">
        <Notice kind="error">Link incompleto. Abra o link do e-mail de novo ou peça outro.</Notice>
        <p className="auth__switch">
          <Link href="/esqueci-senha">Esqueci minha senha</Link>
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Criar nova senha" subtitle="Escolha a nova senha da sua conta.">
      <form className="form" onSubmit={submit} noValidate>
        <div className="field">
          <label htmlFor={ids.password}>Nova senha</label>
          <input
            id={ids.password}
            type="password"
            autoComplete="new-password"
            minLength={LIMITS.minPasswordLength}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <p className="muted small">Mínimo de {LIMITS.minPasswordLength} caracteres.</p>
        </div>
        <div className="field">
          <label htmlFor={ids.confirm}>Repita a nova senha</label>
          <input id={ids.confirm} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </div>
        {error && <Notice kind="error">{error}</Notice>}
        <button type="submit" className="btn btn--primary btn--block btn--lg" disabled={busy}>
          {busy ? 'Salvando…' : 'Salvar nova senha'}
        </button>
      </form>
      <p className="auth__switch">
        <Link href="/esqueci-senha">Pedir um novo link</Link>
      </p>
    </AuthLayout>
  );
}
